import React from 'react';
import { InfoPrincipali, ChecklistType, CHECKLIST_CONFIGS } from '../../types/datamodel';

interface InfoPrincipaliFormProps {
  data: InfoPrincipali;
  checklistType: ChecklistType;
  onChange: (data: InfoPrincipali) => void;
}

export function InfoPrincipaliForm({ data, checklistType, onChange }: InfoPrincipaliFormProps) {
  const config = CHECKLIST_CONFIGS[checklistType];

  const handleChange = (field: keyof InfoPrincipali, value: any) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className="p-6 space-y-8 max-w-5xl">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b pb-3">
          Identificativi Polizza
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Numero Polizza */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Numero Polizza <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.numeroPolizza}
              onChange={(e) => handleChange('numeroPolizza', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Inserisci numero polizza"
              required
            />
          </div>

          {/* Compagnia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compagnia <span className="text-red-500">*</span>
            </label>
            <select
              value={data.compagnia}
              onChange={(e) => handleChange('compagnia', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
              <option value="">Seleziona compagnia...</option>
              <option value="Compagnia A">Compagnia A</option>
              <option value="Compagnia B">Compagnia B</option>
              <option value="Compagnia C">Compagnia C</option>
              <option value="Compagnia D">Compagnia D</option>
            </select>
          </div>

          {/* Tipologia Prodotto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipologia Prodotto
            </label>
            <input
              type="text"
              value={data.tipologiaProdotto}
              onChange={(e) => handleChange('tipologiaProdotto', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Es: Vita, RCA, etc."
            />
          </div>

          {/* Nome Commerciale */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome Commerciale
            </label>
            <input
              type="text"
              value={data.nomeCommerciale}
              onChange={(e) => handleChange('nomeCommerciale', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Nome commerciale del prodotto"
            />
          </div>
        </div>
      </div>

      {/* Contraente */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b pb-3">
          Dati Contraente
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Codice Fiscale */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Codice Fiscale <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.codiceFiscaleContraente}
              onChange={(e) => handleChange('codiceFiscaleContraente', e.target.value.toUpperCase())}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono"
              placeholder="RSSMRA80A01H501U"
              maxLength={16}
              required
            />
          </div>

          {/* Nominativo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nominativo Contraente <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.nominativoContraente}
              onChange={(e) => handleChange('nominativoContraente', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Nome e Cognome"
              required
            />
          </div>
        </div>
      </div>

      {/* Operazione */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b pb-3">
          Dettagli Operazione
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Tipologia Operazione */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipologia Operazione <span className="text-red-500">*</span>
            </label>
            <select
              value={data.tipologiaOperazione}
              onChange={(e) => handleChange('tipologiaOperazione', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
              <option value="">Seleziona tipologia...</option>
              {config.allowedOperations.map(op => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
          </div>

          {/* Data Sottoscrizione */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Sottoscrizione
            </label>
            <input
              type="date"
              value={data.dataSottoscrizione ? data.dataSottoscrizione.toISOString().split('T')[0] : ''}
              onChange={(e) => handleChange('dataSottoscrizione', e.target.value ? new Date(e.target.value) : null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Data Operazione */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Operazione
            </label>
            <input
              type="date"
              value={data.dataOperazione ? data.dataOperazione.toISOString().split('T')[0] : ''}
              onChange={(e) => handleChange('dataOperazione', e.target.value ? new Date(e.target.value) : null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Importo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Importo (€)
            </label>
            <input
              type="number"
              step="0.01"
              value={data.importo || ''}
              onChange={(e) => handleChange('importo', e.target.value ? parseFloat(e.target.value) : null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>
        </div>
      </div>

      {/* Controllo */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b pb-3">
          Informazioni Controllo
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Data Controllo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Controllo <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={data.dataControllo ? data.dataControllo.toISOString().split('T')[0] : ''}
              onChange={(e) => handleChange('dataControllo', e.target.value ? new Date(e.target.value) : null)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>

          {/* Controllo Effettuato */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modalità Controllo <span className="text-red-500">*</span>
            </label>
            <select
              value={data.controlloEffettuato}
              onChange={(e) => handleChange('controlloEffettuato', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            >
              <option value="">Seleziona...</option>
              <option value="remoto">Remoto</option>
              <option value="filiale">Filiale</option>
            </select>
          </div>

          {/* Referente */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Referente <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.referente}
              onChange={(e) => handleChange('referente', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Nome Cognome"
              required
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg"
        >
          Salva Informazioni
        </button>
      </div>
    </div>
  );
}
